/**
 * Project audit tab — scoped audit log for one project.
 *
 * Uses the same /api/audit-log endpoint with `projectId` set, no pagination
 * controls (project-scoped histories are bounded). Both Admin and Assistant
 * can view this — the server allows non-admins to read audit entries for
 * projects they can access.
 */
import { FileText } from 'lucide-react';
import { formatDateTime } from '@procurement/shared';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { DataTable, type ColumnDef } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorPanel } from '@/components/ui/error-panel';
import { Spinner } from '@/components/ui/spinner';
import { useAuditList, type AuditAction, type AuditEntry } from '@/lib/users-audit-queries';
import { parseAuditMetadata } from '@/lib/audit-metadata';

export function ProjectAuditTab({ projectId }: { projectId: string }) {
  const query = useAuditList({ projectId, pageSize: 200 });

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6 text-secondary" />
      </div>
    );
  }
  // Patch 5 — explicit error state.
  if (query.isError || !query.data) {
    return (
      <ErrorPanel
        title="Could not load audit log"
        onRetry={() => query.refetch()}
      />
    );
  }

  const rows = query.data?.items ?? [];

  const columns: ColumnDef<AuditEntry>[] = [
    {
      id: 'when',
      header: 'When',
      width: '170px',
      cell: (r) => (
        <span className="text-xs text-secondary tabular-nums">{formatDateTime(r.occurredAt)}</span>
      ),
    },
    {
      id: 'who',
      header: 'Who',
      width: '140px',
      cell: (r) => <span className="text-primary">{r.userDisplayName}</span>,
    },
    {
      id: 'action',
      header: 'Action',
      width: '180px',
      cell: (r) => <ActionBadge action={r.action} />,
    },
    {
      id: 'entity',
      header: 'Entity',
      width: '150px',
      cell: (r) => (
        <span className="font-mono text-xs text-secondary">
          {r.entityType.replace(/_/g, ' ').toLowerCase()}
        </span>
      ),
    },
    {
      id: 'detail',
      header: 'Detail',
      cell: (r) => <DetailCell entry={r} />,
    },
  ];

  return (
    <Card bodyClassName="p-0">
      {rows.length === 0 ? (
        <EmptyState
          icon={<FileText className="h-6 w-6" />}
          title="No audit entries"
          body="Every change to this project is logged here. Once edits start, the history appears."
        />
      ) : (
        <DataTable<AuditEntry> columns={columns} rows={rows} rowKey={(r) => r.id} />
      )}
    </Card>
  );
}

function ActionBadge({ action }: { action: AuditAction }) {
  const tone =
    action === 'CREATE'
      ? 'success'
      : action.startsWith('DELETE') ||
          action.startsWith('REJECT') ||
          action.startsWith('CANCEL') ||
          action === 'CLOSE_PROJECT'
        ? 'danger'
        : action.startsWith('APPROVE') || action.startsWith('FINALIZE')
          ? 'info'
          : 'neutral';
  return <Badge tone={tone as 'success' | 'danger' | 'info' | 'neutral'}>{action}</Badge>;
}

function DetailCell({ entry }: { entry: AuditEntry }) {
  if (entry.action === 'UPDATE' && entry.fieldName) {
    const oldVal = safeRender(entry.oldValueJson);
    const newVal = safeRender(entry.newValueJson);
    return (
      <div className="text-xs">
        <span className="font-mono text-secondary">{entry.fieldName}:</span>{' '}
        <span className="text-tertiary line-through">{oldVal}</span>
        <span className="mx-1 text-tertiary">→</span>
        <span className="text-primary">{newVal}</span>
      </div>
    );
  }
  // Lifecycle / context actions — patch E6 routes everything through
  // `parseAuditMetadata`. See audit-log.tsx for the symmetric handling.
  const meta = parseAuditMetadata(entry.metadataJson);
  if (meta) {
    if (meta.kind === 'rejection') {
      return <div className="text-xs italic text-secondary">“{meta.comment}”</div>;
    }
    if (meta.kind === 'reason') {
      return <div className="text-xs text-secondary">{meta.label}</div>;
    }
    return (
      <div className="text-xs text-secondary">
        {meta.entries.map((e, i) => (
          <span key={e.key}>
            {i > 0 ? ', ' : ''}
            <span className="font-mono text-secondary">{e.key}:</span> {e.value}
          </span>
        ))}
      </div>
    );
  }
  return <span className="text-xs text-tertiary">—</span>;
}

function formatAuditPrimitiveValue(value: string): string {
  const mapped =
    value === 'OUR_PREMISES'
      ? 'Firm’s premises'
      : value === 'CLIENT_PREMISES'
        ? 'Client premises'
        : value === 'THIRD_PARTY_PREMISES'
          ? 'Third party premises'
          : value;
  return mapped.length > 40 ? `${mapped.slice(0, 40)}…` : mapped;
}

function safeRender(json: string | null): string {
  if (json === null) return '—';
  try {
    const v = JSON.parse(json);
    if (v === null) return '—';
    if (typeof v === 'string') return formatAuditPrimitiveValue(v);
    return JSON.stringify(v);
  } catch {
    return formatAuditPrimitiveValue(json);
  }
}
